/**
 * useGoalAnalysisFlow
 *
 * Shared hook that orchestrates the goal analysis → clarification → generation flow.
 * Used by HomePage and CreateGuidePage when "Analyze first" mode is selected.
 *
 * Uses the async job system: fires a startAnalysis mutation, then
 * subscribes to the Firestore job document via onSnapshot. When the job succeeds,
 * the resultData is parsed as a PlanAnalysisResult and fed to the dialog.
 */

import React, { useState, useRef, useCallback, useEffect } from 'react';
import { useMutation } from '@apollo/client';
import { doc, collection, query, orderBy, onSnapshot } from 'firebase/firestore';
import { START_ANALYSIS } from '../graphql/mutations';
import { auth, db } from '../config/firebase';
import PlanClarificationDialog from '../components/PlanClarificationDialog';

// ============================================================================
// Types
// ============================================================================

export interface PlanAnalysisResult {
  originalPrompt: string;
  goalInterpretation: {
    whatUserWants: string;
    measurableOutcome: string;
    implicitConstraints: string[];
  };
  domainResearch: {
    findings: Array<{
      topic: string;
      summary: string;
      details: string[];
      source?: string;
      confidence: number;
    }>;
    searchQueries: string[];
    sources: string[];
    generatedAt: string;
  };
  clarifyingQuestions: Array<{
    id: string;
    question: string;
    why: string;
    type: 'text' | 'choice' | 'boolean' | 'email' | 'date';
    choices?: string[];
    required: boolean;
    defaultValue?: string;
  }>;
  readyToGenerate: boolean;
  errors?: string[];
}

export interface GenerationPayload {
  prompt: string;
  analysisResult: PlanAnalysisResult;
  clarifyingAnswers: Record<string, string> | null;
  attachmentIds: string[];
}

export interface SkipPayload {
  prompt: string;
  attachmentIds: string[];
}

interface GoalAnalysisFlowOptions {
  onGenerate: (payload: GenerationPayload) => Promise<void>;
  onSkip: (payload: SkipPayload) => Promise<void>;
}

interface UseGoalAnalysisFlowReturn {
  startAnalysis: (text: string, attachmentIds: string[]) => Promise<void>;
  isAnalyzing: boolean;
  isGenerating: boolean;
  dialogElement: React.ReactNode;
}

// ============================================================================
// Helpers
// ============================================================================

/** Get Firebase auth token */
const getAuthToken = async () => {
  const token = await auth.currentUser?.getIdToken();
  if (!token) {
    throw new Error('Failed to get authentication token');
  }
  return token;
};

/** Strip __typename fields from GraphQL response before passing as input */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const stripTypenames = <T extends Record<string, any>>(obj: T): T => {
  if (Array.isArray(obj)) {
    return obj.map(stripTypenames) as unknown as T;
  }
  if (obj && typeof obj === 'object') {
    const result: Record<string, unknown> = {};
    for (const [key, value] of Object.entries(obj)) {
      if (key !== '__typename') {
        result[key] = stripTypenames(value as Record<string, unknown>);
      }
    }
    return result as T;
  }
  return obj;
};

// ============================================================================
// Hook
// ============================================================================

export function useGoalAnalysisFlow(
  options: GoalAnalysisFlowOptions
): UseGoalAnalysisFlowReturn {
  const [dialogOpen, setDialogOpen] = useState(false);
  const [analysisResult, setAnalysisResult] = useState<PlanAnalysisResult | null>(null);
  const [isGenerating, setIsGenerating] = useState(false);
  const [isAnalyzing, setIsAnalyzing] = useState(false);
  const [currentStage, setCurrentStage] = useState<string | null>(null);
  const [stageSubMessage, setStageSubMessage] = useState<string | null>(null);
  const [stageTokens, setStageTokens] = useState<number | null>(null);

  // Store prompt text and attachmentIds so they survive the clarification dialog flow
  const pendingPromptText = useRef('');
  const pendingAttachmentIds = useRef<string[]>([]);

  // Track active subscriptions for cleanup
  const unsubscribeRef = useRef<(() => void) | null>(null);
  const eventsUnsubscribeRef = useRef<(() => void) | null>(null);

  const [startAnalysisMutation] = useMutation(START_ANALYSIS);

  // Stable ref to latest callbacks to avoid stale closures in setTimeout
  const optionsRef = useRef(options);
  optionsRef.current = options;

  // Cleanup subscriptions on unmount
  useEffect(() => {
    return () => {
      if (unsubscribeRef.current) {
        unsubscribeRef.current();
        unsubscribeRef.current = null;
      }
      if (eventsUnsubscribeRef.current) {
        eventsUnsubscribeRef.current();
        eventsUnsubscribeRef.current = null;
      }
    };
  }, []);

  const handleGeneratePlan = useCallback(async (
    analysis: PlanAnalysisResult,
    answers: Record<string, string>
  ) => {
    setDialogOpen(false);
    setIsGenerating(true);
    try {
      const cleanAnalysis = stripTypenames({
        originalPrompt: analysis.originalPrompt,
        goalInterpretation: analysis.goalInterpretation,
        domainResearch: analysis.domainResearch,
        clarifyingQuestions: analysis.clarifyingQuestions,
        readyToGenerate: analysis.readyToGenerate,
      });

      await optionsRef.current.onGenerate({
        prompt: pendingPromptText.current,
        analysisResult: cleanAnalysis,
        clarifyingAnswers: Object.keys(answers).length > 0 ? answers : null,
        attachmentIds: pendingAttachmentIds.current,
      });
    } finally {
      setIsGenerating(false);
      setAnalysisResult(null);
      pendingPromptText.current = '';
      pendingAttachmentIds.current = [];
    }
  }, []);

  const handleSkipClarification = useCallback(async () => {
    setDialogOpen(false);
    setIsGenerating(true);
    try {
      await optionsRef.current.onSkip({
        prompt: pendingPromptText.current,
        attachmentIds: pendingAttachmentIds.current,
      });
    } finally {
      setIsGenerating(false);
      setAnalysisResult(null);
      pendingPromptText.current = '';
      pendingAttachmentIds.current = [];
    }
  }, []);

  const subscribeToJob = useCallback((jobId: string) => {
    // Clean up any existing subscriptions
    if (unsubscribeRef.current) {
      unsubscribeRef.current();
    }
    if (eventsUnsubscribeRef.current) {
      eventsUnsubscribeRef.current();
    }

    const jobDocRef = doc(db, 'generation_jobs', jobId);
    const unsubscribe = onSnapshot(jobDocRef, (snapshot) => {
      const data = snapshot.data();
      if (!data) return;

      setCurrentStage(data.currentStage || null);

      if (data.status === 'SUCCEEDED' && data.resultData) {
        // Parse resultData as PlanAnalysisResult
        const result = data.resultData as PlanAnalysisResult;
        setAnalysisResult(result);
        setIsAnalyzing(false);

        // Unsubscribe — we got the result
        unsubscribe();
        unsubscribeRef.current = null;
        if (eventsUnsubscribeRef.current) {
          eventsUnsubscribeRef.current();
          eventsUnsubscribeRef.current = null;
        }

        // If ready to generate and no required questions, auto-proceed
        if (
          result.readyToGenerate &&
          !result.clarifyingQuestions.some((q) => q.required)
        ) {
          setTimeout(() => {
            handleGeneratePlan(result, {});
          }, 500);
        }
      } else if (data.status === 'FAILED') {
        setIsAnalyzing(false);
        setAnalysisResult({
          originalPrompt: pendingPromptText.current,
          goalInterpretation: {
            whatUserWants: '',
            measurableOutcome: '',
            implicitConstraints: [],
          },
          domainResearch: {
            findings: [],
            searchQueries: [],
            sources: [],
            generatedAt: new Date().toISOString(),
          },
          clarifyingQuestions: [],
          readyToGenerate: false,
          errors: [data.errorMessage || 'Analysis failed'],
        });

        // Unsubscribe — job is done
        unsubscribe();
        unsubscribeRef.current = null;
        if (eventsUnsubscribeRef.current) {
          eventsUnsubscribeRef.current();
          eventsUnsubscribeRef.current = null;
        }
      }
    });

    unsubscribeRef.current = unsubscribe;

    // Subscribe to events subcollection for real-time sub-messages
    const eventsRef = collection(db, 'generation_jobs', jobId, 'events');
    const eventsQuery = query(eventsRef, orderBy('timestamp', 'asc'));
    const eventsUnsub = onSnapshot(eventsQuery, (snapshot) => {
      // Find the latest message and tokens from events
      let latestMsg: string | null = null;
      let latestTokens: number | null = null;
      snapshot.docs.forEach((d) => {
        const data = d.data();
        if (data.message) {
          latestMsg = data.message;
          latestTokens = data.metadata?.tokens ?? null;
        }
      });
      setStageSubMessage(latestMsg);
      setStageTokens(latestTokens);
    });

    eventsUnsubscribeRef.current = eventsUnsub;
  }, [handleGeneratePlan]);

  const startAnalysis = useCallback(async (text: string, attachmentIds: string[]) => {
    if (!text.trim() && attachmentIds.length === 0) return;

    pendingPromptText.current = text.trim();
    pendingAttachmentIds.current = attachmentIds;

    const token = await getAuthToken();

    // Open dialog immediately to show analyzing state
    setDialogOpen(true);
    setAnalysisResult(null);
    setIsAnalyzing(true);
    setCurrentStage(null);

    const { data } = await startAnalysisMutation({
      variables: {
        prompt: text.trim(),
        ...(attachmentIds.length > 0 && { attachmentIds }),
      },
      context: {
        headers: {
          authorization: `Bearer ${token}`,
        },
      },
    });

    if (data?.startAnalysis?.jobId) {
      // Subscribe to job progress via Firestore onSnapshot
      subscribeToJob(data.startAnalysis.jobId);
    }
  }, [startAnalysisMutation, subscribeToJob]);

  const handleDialogClose = useCallback(() => {
    setDialogOpen(false);
    setAnalysisResult(null);
    setIsAnalyzing(false);
    setCurrentStage(null);
    setStageSubMessage(null);
    setStageTokens(null);
    // Clean up subscriptions
    if (unsubscribeRef.current) {
      unsubscribeRef.current();
      unsubscribeRef.current = null;
    }
    if (eventsUnsubscribeRef.current) {
      eventsUnsubscribeRef.current();
      eventsUnsubscribeRef.current = null;
    }
  }, []);

  const handleSubmitWithAnswers = useCallback(
    (answers: Record<string, string>) => {
      if (analysisResult) {
        handleGeneratePlan(analysisResult, answers);
      }
    },
    [analysisResult, handleGeneratePlan]
  );

  const dialogElement = React.createElement(PlanClarificationDialog, {
    open: dialogOpen,
    analysis: analysisResult,
    isAnalyzing,
    currentStage,
    subMessage: stageSubMessage,
    subTokens: stageTokens,
    onClose: handleDialogClose,
    onSubmit: handleSubmitWithAnswers,
    onSkip: handleSkipClarification,
  });

  return {
    startAnalysis,
    isAnalyzing,
    isGenerating,
    dialogElement,
  };
}
